/**
 * Panel layout helpers shared by compatibility checks and mounting systems.
 * Kept separate from compatibility.tsx to avoid circular imports with SolarNest modules.
 */

export function getPanelLayoutDimensions(cellsActive: string[]): { numColumns: number; numRows: number } {
  if (!cellsActive?.length) return { numColumns: 0, numRows: 0 }
  const panels = cellsActive.map((cell) => {
    const coords = cell.split(',')
    const column = Number(coords[0]) * -1
    const row = Number(coords[1])
    return { row, column }
  })
  const smallestColumnCoord = Math.min(...panels.map((p) => p.column))
  if (smallestColumnCoord < 0) panels.forEach((p) => (p.column += Math.abs(smallestColumnCoord)))
  const smallestRowCoord = Math.min(...panels.map((p) => p.row))
  if (smallestRowCoord < 0) panels.forEach((p) => (p.row += Math.abs(smallestRowCoord)))
  let numColumns = 0
  let numRows = 0
  panels.forEach(({ row, column }) => {
    if (row + 1 > numRows) numRows = row + 1
    if (column + 1 > numColumns) numColumns = column + 1
  })
  return { numColumns, numRows }
}

export function hasUniformRowLengths(cellsActive: string[]): boolean {
  if (!cellsActive?.length) return true // Empty grids are considered uniform
  const panels = cellsActive.map((cell) => {
    const coords = cell.split(',')
    const column = Number(coords[0]) * -1
    const row = Number(coords[1])
    return { row, column }
  })
  const smallestColumnCoord = Math.min(...panels.map((p) => p.column))
  if (smallestColumnCoord < 0) panels.forEach((p) => (p.column += Math.abs(smallestColumnCoord)))
  const smallestRowCoord = Math.min(...panels.map((p) => p.row))
  if (smallestRowCoord < 0) panels.forEach((p) => (p.row += Math.abs(smallestRowCoord)))

  // Group panels by row and count active modules per row
  const rowCounts = new Map<number, number>()
  panels.forEach(({ row }) => {
    rowCounts.set(row, (rowCounts.get(row) || 0) + 1)
  })

  // Check if all rows have the same count
  const counts = Array.from(rowCounts.values())
  if (counts.length === 0) return true // No rows means uniform
  if (counts.length === 1) return true // Single row is always uniform
  const firstCount = counts[0]
  return counts.every((count) => count === firstCount)
}
